import { h, defineComponent, onBeforeMount, onMounted, onUnmounted, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, col, cols, container, loader, pageSubtitle, pageTitle } from '@/ui/components';
import { HubspotContactSalesModal } from '@/ui/components/modals/HubspotContactSalesModal';
import { pushRoute } from '@/ui/helpers/uiHelper';
import { getOrganizationUsersCount, hasSubscription, isInFreeTrial, isOnFreePlan, justFinishedFreeTrial, user } from '@/ui/auth/auth';
import { contactSalesModal } from '@/ui/subscription/subscription';
import { ChecklistSubscription } from '@/ui/pages/subscription/SubscribeChecklist';
import { SecurePayment } from '@/ui/pages/subscription/SecurePayment';
import { SubscribeButton } from '@/ui/components/subscription/SubscribeButton';
import { country, currency, finalCharge, getList, numberSeats, professionalPlan } from '@/services/chargebee/availablePlans';
import { BoxSelectionsCb } from '@/ui/pages/subscription/Chargebee/subscribe/BoxSelections';
import { CbSummary } from '@/ui/pages/subscription/Chargebee/subscribe/Summary';
import { CbSelectUsers } from '@/ui/pages/subscription/Chargebee/subscribe/SelectUsers';
import CbCountrySelect from '@/ui/pages/subscription/Chargebee/subscribe/CountrySelect.vue';
import { chargebee, getChargeBeeInstance } from '@/services/chargebee';
import { getCurrency, geolocate } from '@/services/api/resources/geolocation';
import { checkout, hostedPage, loading, openCheckout } from '@/services/chargebee/checkout';
import { useTask } from '@/ui/concerns';
import { useCurrentRoute } from '@/ui/routes';

export const SubscribePageCb = defineComponent({
  name: 'SubscribePageCb',
  setup() {
    const route = useCurrentRoute();
    const minNumberSeats = getOrganizationUsersCount();
    const isCurrencyForced = user.value.organization.forceCurrency;
    const countryFromUrl = route.value?.query?.country;
    country.value = countryFromUrl ? String(countryFromUrl) : 'DE';
    currency.value = user.value.organization.preferredCurrency;

    const getCurrencyTask = (country: string) => useTask(
      () => getCurrency(country),
      response => {
        if (response.data.currency) {
          pushRoute({
            name: 'subscribe',
            query: { ...route.value.query, country },
          });

          currency.value = response.data.currency;
        }
      },
    );

    const geolocateTask = () => useTask(
      () => geolocate(),
      response => {
        if (response.data.currency && response.data.country) {
          currency.value = response.data.currency;
          country.value = !countryFromUrl ? response.data.country : country.value;
        }
      },
    );

    if (Boolean(countryFromUrl) && !isCurrencyForced) {
      getCurrencyTask(String(countryFromUrl)).run();
    }
    numberSeats.value = minNumberSeats;
    const checkUserHasFreePlan = () => {
      if (hasSubscription() && !isOnFreePlan.value && !isInFreeTrial.value) {
        pushRoute({ name: 'home' });
      }
    };

    onBeforeMount(checkUserHasFreePlan);

    watch(hostedPage, () => openCheckout());

    onMounted(async () => {
      if (!isCurrencyForced && !countryFromUrl) {
        geolocateTask().run();
      }
      await getChargeBeeInstance();
    });

    onUnmounted(() => {
      chargebee.value?.closeAll();
    });

    return function render() {
      const firstColumn = () => col([
        box([
          pageSubtitle(t('subscription.choose-billing-frequency')),
          h(BoxSelectionsCb),
          h(CbSelectUsers),
          h(CbCountrySelect, {
            props: {
              country: country.value,
              currency: currency.value,
            },
            class: 'subscribe_page__section',
            on: {
              countryChange: (newCountry: string) => {
                country.value = newCountry;

                if (!isCurrencyForced) {
                  getCurrencyTask(newCountry).run();
                }
              },
            },
          }),
          h(CbSummary, {
            props: {
              numberSeats: numberSeats.value,
              pricePerUser: professionalPlan.value?.amount,
              finalCharge: finalCharge.value,
            },
          }),
          h(SubscribeButton, {
            props: {
              planId: professionalPlan.value?.id,
              seats: numberSeats.value.toString(),
              btnClass: 'subscribe_page__cb-pay-btn subscribe_page__section',
              loading: loading.value,
            },
            on: { checkout },
          }),
          h(
            'div',
            { class: 'subscribe_page__billing-date' },
          ),
        ]),
        h(SecurePayment),
      ], false, { 'is-8': true });

      return loader('div', getList, [
        h(HubspotContactSalesModal, {
          props: { active: contactSalesModal.value },
          on: {
            close: () => {
              contactSalesModal.value = false;
            },
          },
        }),
        container(
          [
            justFinishedFreeTrial.value
              ? h('div', { class: 'subscribe_page__titles-container' }, [
                pageSubtitle(t('trial-end.trial-end')),
                pageTitle(t('trial-end.stay-in-plan')),
              ])
              : pageTitle(t('subscription.go-professional'), 'subscribe_page__title'),
            cols([
              firstColumn(),
              col([h(ChecklistSubscription)]),
            ], 'subscribe_page__outer-cols'),
          ],
          'default',
          'subscribe_page',
        ),
      ]);
    };
  },
});
